// fileApiV2.ts
import axios from "axios";
import { BaseURl } from "../../utils";  // kept for parity with your codebase

// helper to get headers (reads token at call time)
const getAuthHeaders = (isForm = false) => {
  const token: string | null = localStorage.getItem("token");
  const headers: Record<string, string> = {
    // Let axios set proper multipart/form-data boundary when using FormData
    "Content-Type": isForm ? "multipart/form-data" : "application/json",
  };
  if (token) headers["x-access-token"] = token;
  return headers;
};

// Create a new file
export const createFileApi = async (modify: any) => {
  try {
    const response = await axios.post(
      `${BaseURl}file/create`,
      modify,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("createFileApi error:", error);
    throw error;
  }
};

// Get all files for a mode (supports optional pagination)
export const getFilesByModeApi = async (modeid: any, page_no = 1, page_size = 100) => {
  try {
    const response = await axios.get(
      `${BaseURl}file/getall/${modeid}?page_no=${page_no}&page_size=${page_size}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("getFilesByModeApi error:", error);
    throw error;
  }
};

// Get file by ID
export const getFileByIdApi = async (fileId: any) => {
  try {
    const response = await axios.get(
      `${BaseURl}file/getFileById/${fileId}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("getFileByIdApi error:", error);
    throw error;
  }
};

// Get all trash files for a mode
export const getTrashFilesApi = async (modeId: any, page_no = 1, page_size = 100) => {
  try {
    const response = await axios.get(
      `${BaseURl}file/getallTrash/${modeId}?page_no=${page_no}&page_size=${page_size}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("getTrashFilesApi error:", error);
    throw error;
  }
};

// Get all starred files for a mode
export const getStarredFilesApi = async (modeId: any, page_no = 1, page_size = 100) => {
  try {
    const response = await axios.get(
      `${BaseURl}file/getallstarredfile/${modeId}?page_no=${page_no}&page_size=${page_size}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("getStarredFilesApi error:", error);
    throw error;
  }
};

// Update file
export const updateFileApi = async (data: { id: any; modify: any }) => {
  const { id, modify } = data;
  try {
    const response = await axios.put(
      `${BaseURl}file/update/${id}`,
      modify,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("updateFileApi error:", error);
    throw error;
  }
};

// Upload image file
export const uploadImageApi = async (data: { id: string; modeId: any; folderId: any; image: File }) => {
  const { id, modeId, folderId, image } = data;
  try {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("id", id); // MongoDB file ID (compulsory)

    const response = await axios.put(
      `${BaseURl}file/UploadImage/${id}/${folderId}`,
      formData,
      { headers: getAuthHeaders(true) }
    );
    return response.data;
  } catch (error) {
    console.error("uploadImageApi error:", error);
    throw error;
  }
};

// Upload document file
export const uploadDocumentApi = async (data: { id: string; modeId: any; folderId: any; file: File }) => {
  const { id, modeId, folderId, file } = data;
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", id); // MongoDB file ID (compulsory)

    const response = await axios.put(
      `${BaseURl}file/UploadFile/${id}/${folderId}`,
      formData,
      { headers: getAuthHeaders(true) }
    );
    return response.data;
  } catch (error) {
    console.error("uploadDocumentApi error:", error);
    throw error;
  }
};

// Upload video file
export const uploadVideoApi = async (data: { id: string; modeId: any; folderId: any; video: File }) => {
  const { id, modeId, folderId, video } = data;
  try {
    const formData = new FormData();
    formData.append("video", video);
    formData.append("id", id); // MongoDB file ID (compulsory)

    const response = await axios.put(
      `${BaseURl}file/UploadVideo/${id}/${folderId}`,
      formData,
      { headers: getAuthHeaders(true) }
    );
    return response.data;
  } catch (error) {
    console.error("uploadVideoApi error:", error);
    throw error;
  }
};

// Delete all files by mode
export const deleteAllFilesApi = async (modeId: any) => {
  try {
    const response = await axios.delete(
      `${BaseURl}file/deleteAllFile/${modeId}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("deleteAllFilesApi error:", error);
    throw error;
  }
};

// Soft delete file (move to trash)
export const softDeleteFileApi = async (id: any) => {
  try {
    const response = await axios.delete(
      `${BaseURl}file/fileRemove/${id}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("softDeleteFileApi error:", error);
    throw error;
  }
};

// Permanently delete file
export const deleteFilePermanentlyApi = async (id: any) => {
  try {
    const response = await axios.delete(
      `${BaseURl}file/deleteFileByIdPermanently/${id}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("deleteFilePermanentlyApi error:", error);
    throw error;
  }
};

// Delete multiple files (expects fileIds: any[])
export const deleteMultipleFilesApi = async (fileIds: any[]) => {
  try {
    const response = await axios.post(
      `${BaseURl}file/delete-multiple`,
      { fileIds },
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("deleteMultipleFilesApi error:", error);
    throw error;
  }
};

// Share multiple files
export const shareMultipleFilesApi = async (data: { fileIds: string[]; userIds: string[] }) => {
  try {
    const response = await axios.post(
      `${BaseURl}file/share-multiple`,
      data,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("shareMultipleFilesApi error:", error);
    throw error;
  }
};

// Mark file as starred
export const markFileAsStarredApi = async (id: any) => {
  try {
    const response = await axios.put(
      `${BaseURl}file/filemarkedstarred/${id}`,
      {},
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("markFileAsStarredApi error:", error);
    throw error;
  }
};

// Unmark file as starred
export const unmarkFileAsStarredApi = async (id: any) => {
  try {
    const response = await axios.put(
      `${BaseURl}file/fileunmarkedstarred/${id}`,
      {},
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("unmarkFileAsStarredApi error:", error);
    throw error;
  }
};

// Restore file from trash
export const restoreFileApi = async (id: any) => {
  try {
    const response = await axios.put(
      `${BaseURl}file/restoreFile/${id}`,
      {},
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("restoreFileApi error:", error);
    throw error;
  }
};

// Get shared files for a user
export const getSharedFilesApi = async (userId: string, page_no = 1, page_size = 100) => {
  try {
    const url = `${BaseURl}file/shared/${userId}?page_no=${page_no}&page_size=${page_size}`;
    const response = await axios.get(url, {
      headers: getAuthHeaders(false),
    });
    return response.data;
  } catch (error) {
    console.error("getSharedFilesApi error:", error);
    throw error;
  }
};

// Remove a shared file for a user
export const removeSharedFileApi = async (fileId: string, userId: string) => {
  try {
    const response = await axios.delete(
      `${BaseURl}file/shared/${fileId}/${userId}`,
      { headers: getAuthHeaders(false) }
    );
    return response.data;
  } catch (error) {
    console.error("removeSharedFileApi error:", error);
    throw error;
  }
};
